import React, { useState } from 'react';
import { Container, Row, Col, Card, Accordion, ListGroup, Button, Modal, Alert } from 'react-bootstrap';
import ContactForm from '../components/ContactForm'; // Import the reusable form
import { Helmet } from 'react-helmet-async';
import teamsData from '../data/teams.json';

// Define a structure for the team data
interface Team {
  name: string;
  manager: string;
  email: string;
  phone: string;
  registrationLink: string;
  trainingInfo: string;
}

const Teams: React.FC = () => {
  const [showModal, setShowModal] = useState(false);

  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = () => setShowModal(true);

  const boysTeams: Team[] = teamsData.boysTeams;
  const girlsWomensTeams: Team[] = teamsData.girlsWomensTeams;
  // Convert the team names into objects with placeholder data
  // const boysTeams: Team[] = [
  //   'U7s Park', 'U7s Royals', 'U8s Park', 'U8s Royals', 'U9s Park', 'U9s Royals', 'U10s Park', 'U10s Royals', 'U10s Rovers', 'U11s Park', 
  //   'U12s Park', 'U13s Park', 'U13s Royals', 'U13s Rovers', 'U14s Royals', 'U14s Rovers', 'U15s Royals', 'U16s Park', 'U16s Royals', 'U17s Park', 'U17s Royals', 'U18s Royals', 'U18s Park'
  // ].map(name => ({
  //   name,
  //   manager: '[Manager Name Here]',
  //   email: '[manager.email@example.com]',
  //   phone: '[Phone Number Here]',
  //   registrationLink: '#register',
  //   trainingInfo: '[Location], [Day] @ [Time]'
  // }));

  // const girlsWomensTeams: Team[] = [
  //   'U10s Park', 'U12s Park', 'U15s Park', 'U15s Royals', 'U16s Park', 'Ladies', 'Sundays'
  // ].map(name => ({
  //   name,
  //   manager: '[Manager Name Here]',
  //   email: '[manager.email@example.com]',
  //   phone: '[Phone Number Here]',
  //   registrationLink: '#register',
  //   trainingInfo: '[Location], [Day] @ [Time]'
  // }));

  // Helper function to render an accordion for a list of teams
  const renderTeamAccordion = (teams: Team[], type: 'boys' | 'girls') => (
    <Accordion flush>
      {teams.map((team, index) => (
        <Accordion.Item eventKey={`${type}-${index}`} key={`${type}-${index}`}>
          <Accordion.Header>{team.name}</Accordion.Header>
          <Accordion.Body>
            <ListGroup variant="flush">
              <ListGroup.Item><strong>Manager:</strong> {team.manager}</ListGroup.Item>
              {team.phone && team.phone.trim() !== '' && (
                <ListGroup.Item><strong>Phone:</strong> {team.phone}</ListGroup.Item>
              )}
              <ListGroup.Item><strong>Training:</strong> {team.trainingInfo}</ListGroup.Item>
              <ListGroup.Item>
                <div className="d-flex gap-2">
                  {renderEmailButtons(team.email)}
                  {team.registrationLink && team.registrationLink.trim() !== '' && team.registrationLink !== '#register' && (
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      href={team.registrationLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Player Registration
                    </Button>
                  )}
                </div>
              </ListGroup.Item>
            </ListGroup>
          </Accordion.Body>
        </Accordion.Item>
      ))}
    </Accordion>
  );

  const renderEmailButtons = (emails: string) => {
    // Split by comma, semicolon, or ampersand and clean up whitespace
    const emailList = emails.split(/[,;&]/).map(email => email.trim()).filter(email => email.length > 0);
    
    if (emailList.length === 0) {
      return null;
    }
    
    // Join all emails with commas for the mailto link
    const mailtoLink = `mailto:${emailList.join(',')}`;
    
    // Determine button text based on number of managers
    const buttonText = emailList.length === 1 ? 'Email Manager' : `Email Managers (${emailList.length})`;
    
    return (
      <Button 
        variant="outline-primary" 
        size="sm" 
        href={mailtoLink}
      >
        {buttonText}
      </Button>
    );
  };

  return (
    <>
      <Helmet>
          <title>Ashridge Park FC | Grassroots Football Club in Wokingham</title>
          <meta name="description" content="Welcome to Ashridge Park FC, a charter standard community football club in Wokingham, Berkshire, offering youth and adult football for all ages." />
      </Helmet>
      <Container className="p-3 my-4">
        <h2 className="mb-4">OUR TEAMS</h2>

        <Alert variant="primary">
          <Alert.Heading>Interested in Joining?</Alert.Heading>
          <div className="d-flex justify-content-between align-items-center">
            <p className="mb-0">
              If you are interested in joining a specific team, please find their details below and contact the manager directly.
              <br />
              For all general enquiries, or if you're unsure which team is right for you, please use our general contact form.
            </p>
            <Button onClick={handleShowModal} variant="outline-primary" className="ms-3">
              Contact Us
            </Button>
          </div>
        </Alert>
        
        <Row>
          {/* Boys Teams Column */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header as="h5" style={{ backgroundColor: '#254E97', color: 'white' }}>
                Boys/Mixed Teams
              </Card.Header>
              {renderTeamAccordion(boysTeams, 'boys')}
            </Card>
          </Col>

          {/* Girls & Womens Teams Column */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header as="h5" style={{ backgroundColor: '#254E97', color: 'white' }}>
                Girls & Womens Teams
              </Card.Header>
              {renderTeamAccordion(girlsWomensTeams, 'girls')}
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal show={showModal} onHide={handleCloseModal} centered data-bs-theme="dark">
        <Modal.Header closeButton style={{ backgroundColor: '#254E97', color: 'white' }}>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#3a62aa', color: 'white' }}>
          <ContactForm />
        </Modal.Body>
      </Modal>
    </>

  );
};

export default Teams;